import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

const STORAGE_KEY = 'gridDebugEnabled';
export const GRID_DEBUG_EVENT = 'gridDebugChange';

const readPersisted = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // sessionStorage can throw in private browsing / sandboxed iframes.
    return false;
  }
};

const writePersisted = (enabled: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (enabled) {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore
  }
};

export const setGridDebugEnabled = (enabled: boolean): void => {
  writePersisted(enabled);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(GRID_DEBUG_EVENT, { detail: enabled })
    );
  }
};

export const isGridDebugEnabled = (): boolean => readPersisted();

/**
 * Debug flag for the grid card renderer. Mirrors the `highlightBundles`
 * pattern — a query param toggles the flag and persists it across the
 * current browser tab session via sessionStorage:
 *
 * - `?grid_debug=1` (or `=true`) → enable + persist for the session
 * - `?grid_debug=0` (or `=false`) → disable + clear
 * - Otherwise → read from sessionStorage (so navigating away keeps it on)
 *
 * When enabled, each card renders an info affordance that opens the
 * GridDebug modal with raw product/asset data, data-source deep-links,
 * and CMS variables.
 */
const useShowIndexInfo = (): boolean => {
  const { asPath, isReady } = useRouter();
  const [showIndexInfo, setShowIndexInfo] = useState(false);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const search = asPath.split('?')[1] || '';
    const queryValue = new URLSearchParams(search).get('grid_debug');

    if (queryValue === '1' || queryValue === 'true') {
      writePersisted(true);
      setShowIndexInfo(true);
    } else if (queryValue === '0' || queryValue === 'false') {
      writePersisted(false);
      setShowIndexInfo(false);
    } else {
      setShowIndexInfo(readPersisted());
    }
  }, [asPath, isReady]);

  useEffect(() => {
    const handler = (event: Event): void => {
      setShowIndexInfo(Boolean((event as CustomEvent<boolean>).detail));
    };
    window.addEventListener(GRID_DEBUG_EVENT, handler);
    return () => window.removeEventListener(GRID_DEBUG_EVENT, handler);
  }, []);

  return showIndexInfo;
};

export default useShowIndexInfo;
